import { useState } from "react";
import { useAuth, useCart, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Search, Package, Plus } from "lucide-react";

export default function SearchProduct() {
  const { getAuthHeader } = useAuth();
  const { addToCart } = useCart();
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter a search term");
      return;
    }

    setLoading(true);
    setHasSearched(true);
    try {
      const response = await axios.get(
        `${API}/products/search?q=${encodeURIComponent(searchQuery)}`,
        getAuthHeader()
      );
      setResults(response.data);
      if (response.data.length === 0) {
        toast.info("No products found");
      }
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product);
  };

  return (
    <div className="animate-fade-in" data-testid="search-product-page">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Search className="w-5 h-5" />
            Search Product
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Search Bar */}
          <div className="flex gap-3 mb-6">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by product name or barcode..."
              className="flex-1 h-12"
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              data-testid="search-input"
            />
            <Button 
              onClick={handleSearch} 
              disabled={loading}
              className="h-12 px-6"
              data-testid="search-btn"
            >
              {loading ? (
                <span className="loading-spinner" />
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </>
              )}
            </Button>
          </div>

          {/* Results */}
          {!hasSearched ? (
            <div className="text-center py-12">
              <Search className="w-16 h-16 mx-auto text-slate-200 mb-4" />
              <p className="text-slate-500">Enter a product name or barcode to search</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 mx-auto text-slate-200 mb-4" />
              <p className="text-slate-500">No products found for "{searchQuery}"</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border">
              <table className="cart-table">
                <thead>
                  <tr>
                    <th>Barcode</th>
                    <th>Product Name</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((product) => (
                    <tr key={product.id} className="hover:bg-slate-50">
                      <td className="font-mono text-slate-500">{product.barcode}</td>
                      <td className="font-medium">{product.name}</td>
                      <td>
                        <span className="px-2 py-1 bg-slate-100 rounded text-xs font-medium">
                          {product.category}
                        </span>
                      </td>
                      <td className="font-mono font-bold text-blue-600">
                        ${product.price.toFixed(2)}
                      </td>
                      <td>
                        <span className={`font-mono ${product.stock > 10 ? 'text-green-600' : product.stock > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                          {product.stock}
                        </span>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          onClick={() => handleAddToCart(product)}
                          disabled={product.stock === 0}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`add-to-cart-${product.id}`}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {results.length > 0 && (
            <p className="text-sm text-slate-500 mt-4">
              Found {results.length} product(s)
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
